import React from 'react';
import { motion, useScroll, useTransform, MotionValue } from 'motion/react';
import { Link } from 'react-router-dom';
import { Compass, ArrowRight, Activity, FileSearch, CheckCircle, Mail } from 'lucide-react';

const AnimatedGradientText = ({ children, className = "" }: { children: React.ReactNode, className?: string }) => (
  <span className={`pb-1 md:pb-2 text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 via-teal-500 to-emerald-600 dark:from-emerald-400 dark:via-teal-400 dark:to-emerald-400 animate-gradient bg-[length:200%_auto] ${className}`}>
    {children}
  </span>
);

interface LandingHeroProps {
  heroY: MotionValue<number>;
  heroOpacity: MotionValue<number>;
}

export function LandingHero({ heroY, heroOpacity }: LandingHeroProps) {
  return (
    <section className="px-6 relative flex flex-col items-center">
      <motion.div style={{ y: heroY, opacity: heroOpacity }} className="max-w-4xl mx-auto text-center relative z-20">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="inline-flex items-center gap-2 px-3 py-1.5 mb-8 rounded-full bg-white/60 dark:bg-white/5 border border-slate-200 dark:border-white/5 backdrop-blur-md"
        >
          <Compass className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
          <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">The intelligent application toolkit</span>
        </motion.div>

        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="text-5xl md:text-7xl font-black tracking-tight leading-[1.1] mb-6 text-slate-900 dark:text-white"
        >
          Discover open roles & craft <AnimatedGradientText>job-winning resumes</AnimatedGradientText> instantly.
        </motion.h1>

        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="mt-6 text-xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto font-medium leading-relaxed"
        >
          Use AI to find fresh job postings tailored to you, then instantly reshape your resume and cover letter to match them. Stop hunting blindly and start applying with confidence.
        </motion.p>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link to="/app" className="group px-8 py-4 w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white rounded-full text-lg font-bold transition-all dark:shadow-sm dark:hover:shadow-md flex items-center justify-center gap-2 active:scale-95">
            <span className="relative z-10 flex items-center gap-2">
              Find Jobs & Tailor Resume <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </span>
          </Link>
          <a 
            href="#how-it-works" 
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="px-8 py-4 w-full sm:w-auto bg-white/50 dark:bg-white/5 text-slate-800 dark:text-slate-200 border border-slate-300 dark:border-white/10 rounded-full text-lg font-bold transition-all hover:bg-white dark:hover:bg-white/10 flex items-center justify-center gap-2"
          >
             See How It Works
          </a>
        </motion.div>
      </motion.div>

      {/* Hero Visual Preview */}
      <motion.div
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-6xl mt-20 relative"
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-slate-300/30 dark:bg-emerald-500/20 blur-[120px] pointer-events-none -z-10 rounded-full transform-gpu will-change-transform" />
        
        <div className="relative flex flex-col lg:flex-row gap-6 lg:gap-8 items-center bg-transparent">
           
           {/* Base Resume */}
           <div className="flex-1 w-full bg-white dark:bg-slate-900/80 backdrop-blur-md rounded-2xl border border-slate-200 dark:border-slate-800 dark:shadow-md p-6 lg:opacity-60 lg:scale-95 origin-bottom lg:origin-right">
              <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Your Base Resume</div>
              <div className="w-1/3 h-5 bg-slate-200 dark:bg-slate-700 rounded mb-6" />
              <div className="space-y-3 mb-6">
                <div className="w-full h-3 bg-slate-100 dark:bg-slate-800 rounded" />
                <div className="w-5/6 h-3 bg-slate-100 dark:bg-slate-800 rounded" />
                <div className="w-4/5 h-3 bg-slate-100 dark:bg-slate-800 rounded" />
              </div>
              <div className="space-y-3">
                <div className="w-full h-3 bg-slate-100 dark:bg-slate-800 rounded" />
                <div className="w-3/4 h-3 bg-slate-100 dark:bg-slate-800 rounded" />
              </div>
           </div>

           {/* AI Engine */}
           <div className="flex-shrink-0 flex flex-col items-center justify-center gap-3 relative z-10">
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 8, repeat: Infinity, ease: "linear" }} className="absolute inset-0 rounded-full bg-emerald-500/10 dark:bg-emerald-500/20 blur-xl" />
              <div className="relative w-16 h-16 rounded-full bg-gradient-to-tr from-emerald-500 to-teal-500 dark:shadow-lg dark:shadow-emerald-500/30 flex items-center justify-center text-white border-4 border-white dark:border-slate-950">
                <Activity className="w-6 h-6" />
              </div>
              <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-3 py-1.5 rounded-full dark:shadow-sm text-xs font-bold flex items-center gap-1.5">
                <FileSearch className="w-3.5 h-3.5 text-emerald-500" />
                <span className="text-slate-700 dark:text-slate-300">Matching JD...</span>
              </div>
           </div>

           {/* Tailored Result */}
           <div className="flex-[1.2] w-full bg-white dark:bg-slate-900 rounded-2xl border-2 border-emerald-500 dark:shadow-md dark:shadow-sm dark:shadow-emerald-900/10 p-6 relative overflow-hidden group">
              <div className="absolute top-0 right-0 bg-emerald-500 text-white text-[10px] sm:text-xs font-bold px-3 py-1.5 rounded-bl-xl dark:shadow-md z-20 flex gap-1 items-center">
                <CheckCircle className="w-3.5 h-3.5" /> 98% MATCH
              </div>
              
              <div className="flex gap-2 mb-6 pointer-events-none">
                 <span className="text-[10px] font-bold px-2 py-1 bg-slate-100 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded">ATS-Friendly</span>
                 <span className="text-[10px] font-bold px-2 py-1 bg-slate-100 dark:bg-teal-500/10 text-teal-600 dark:text-teal-400 rounded">Role-Specific</span>
              </div>

              <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Tailored Application</div>
              <div className="w-1/3 h-5 bg-emerald-100 dark:bg-emerald-900/50 rounded mb-6" />
              
              <div className="space-y-3 relative z-10">
                <div className="w-full h-3 bg-slate-100 dark:bg-slate-800 rounded relative overflow-hidden">
                   <motion.div initial={{ width: "0%" }} animate={{ width: "100%" }} transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }} className="absolute inset-y-0 left-0 bg-emerald-400/40" />
                </div>
                <div className="w-full h-3 bg-slate-100 dark:bg-slate-800 rounded" />
                <div className="w-5/6 h-3 bg-slate-100 dark:bg-slate-800 rounded" />
              </div>
              
              <div className="h-px w-full bg-slate-100 dark:bg-slate-800 my-6" />
              
              <div className="flex gap-3 items-start">
                 <Mail className="w-5 h-5 text-emerald-500 mt-1" />
                 <div className="space-y-2 flex-1 relative z-10">
                   <div className="text-xs font-bold text-slate-800 dark:text-slate-200 mb-2">Cover Letter Drafted</div>
                   <div className="w-full h-2 bg-slate-100 dark:bg-slate-800 rounded" />
                   <div className="w-3/4 h-2 bg-slate-100 dark:bg-slate-800 rounded" />
                 </div>
              </div>
           </div>

        </div>
      </motion.div>
    </section>
  );
}
